// Property-tree browse screen. Ported from
// designproject/files/screens-tree.jsx (`TreeScreen`) to Preact + TS strict.
// Phase 2.1 (hh-00e). Uses real backend data:
//   GET /api/v1/properties/tree  — properties → rooms → items + equipment
//   GET /api/v1/items/orphans    — items with no place (synthetic top group)
//
// Container nesting: the tree endpoint does not currently project
// `containerId` on items, but the type allows it. When present, an item whose
// id is referenced by another item's `containerId` is treated as a container
// and renders its contents one level deeper.

import type { JSX } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { T, Icon, Chip, entityIcon, isIconName } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { properties as propertiesApi, items as itemsApi, humanizeError } from '../../api';
import type {
  PropertyTreeNode,
  RoomNode,
  ItemNode,
  EquipmentNode,
} from '../../api/endpoints/properties';
import type { OrphanItem } from '../../api/endpoints/items';

interface TreeScreenProps {
  readonly router: RouterApi;
}

type LoadState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | {
      readonly kind: 'ready';
      readonly properties: readonly PropertyTreeNode[];
      readonly orphans: readonly OrphanItem[];
    };

// Два сегмента экрана (aidepo-63.6). Дефолт — «Вещи».
type Tab = 'items' | 'rooms';

// Варианты сортировки плоского списка вещей (aidepo-63.6). Дефолт — «Имя».
type ItemsSort = 'name' | 'place' | 'recent';

const SORT_OPTIONS: ReadonlyArray<{ readonly value: ItemsSort; readonly label: string }> = [
  { value: 'name', label: 'Имя (А-Я)' },
  { value: 'place', label: 'Место' },
  { value: 'recent', label: 'Недавно добавленные' },
];

const ORPHAN_GROUP_ID = '__orphans__';

// Подпись места для вещи без привязки в плоском списке (aidepo-63.6).
const NO_PLACE_LABEL = '— без места';

// Плоская строка для вкладки «Вещи»: вещь из дерева или orphan, приведённые
// к единому виду. Собирается из уже загруженных данных (aidepo-63.6).
interface FlatItem {
  readonly id: string;
  readonly name: string;
  readonly icon: string | null;
  readonly quantity: number;
  readonly isOrphan: boolean;
  /** Человекочитаемое место («Квартира · Кухня») или NO_PLACE_LABEL у orphan. */
  readonly place: string;
  /** ISO-время создания для сортировки «Недавно»; null → считаем старейшим. */
  readonly createdAt: string | null;
}

export function TreeScreen({ router }: TreeScreenProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  // Expanded node ids. Toggling only mutates this set — the scroll container
  // is never remounted, so scroll position is preserved (case-10).
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const [tab, setTab] = useState<Tab>('items');
  const [sort, setSort] = useState<ItemsSort>('name');
  // Локальный фильтр по подстроке в name, регистронезависимо (aidepo-63.6).
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    void Promise.all([
      propertiesApi.getPropertyTree(),
      itemsApi.listOrphans(),
    ])
      .then(([tree, orphans]) => {
        if (cancelled) return;
        setState({
          kind: 'ready',
          properties: tree.properties,
          orphans: orphans.items,
        });
        // Expand all properties by default so populated trees read at a glance.
        setExpanded(new Set(tree.properties.map((p) => p.id)));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({ kind: 'error', message: describeError(err) });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = (id: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Плоский список всех вещей — собираем один раз на готовых данных. Пересчёт
  // только при смене данных, фильтр/сортировка применяются ниже по запросу.
  const flatItems = useMemo<readonly FlatItem[]>(
    () => (state.kind === 'ready' ? collectFlatItems(state.properties, state.orphans) : []),
    [state],
  );

  return (
    <div style={containerStyle}>
      <ScreenHeader
        title="Имущество"
        subtitle={subtitleFor(state)}
        trailing={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={() => router.go('changes')}
              aria-label="История изменений"
              style={iconButtonStyle}
            >
              <Icon name="history" size={19} color={T.ink2} />
            </button>
            <button
              type="button"
              onClick={() => router.openOverlay('add_picker')}
              aria-label="Добавить вручную"
              style={addButtonStyle}
            >
              <Icon name="plus" size={20} color="#fff" />
            </button>
          </div>
        }
      />
      <div style={controlsStyle}>
        <div style={segmentRowStyle}>
          <SegmentButton label="Вещи" active={tab === 'items'} onClick={() => setTab('items')} />
          <SegmentButton
            label="Помещения"
            active={tab === 'rooms'}
            onClick={() => setTab('rooms')}
          />
        </div>
        <div style={searchBoxStyle}>
          <Icon name="search" size={18} color={T.ink3} />
          <input
            value={query}
            onInput={(e) => setQuery(e.currentTarget.value)}
            placeholder="Фильтр по названию…"
            aria-label="Фильтр по названию"
            enterkeyhint="search"
            style={inputStyle}
          />
          {query.length > 0 && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Очистить фильтр"
              style={clearButtonStyle}
            >
              <Icon name="close" size={14} color={T.ink3} />
            </button>
          )}
        </div>
      </div>
      <div style={scrollStyle}>
        {tab === 'items'
          ? renderItemsTab(state, flatItems, sort, setSort, query, router)
          : renderRoomsTab(state, expanded, toggle, query, router)}
      </div>
    </div>
  );
}

// ─── Tab bodies ──────────────────────────────────────────────────────────────

function renderItemsTab(
  state: LoadState,
  flatItems: readonly FlatItem[],
  sort: ItemsSort,
  setSort: (s: ItemsSort) => void,
  query: string,
  router: RouterApi,
): JSX.Element {
  if (state.kind === 'loading') return <CenteredNote text="Загрузка…" />;
  if (state.kind === 'error') {
    return <CenteredNote text={`Не удалось загрузить: ${state.message}`} tone="danger" />;
  }
  if (flatItems.length === 0) {
    return <CenteredNote text="Пусто — добавь первую вещь голосом или через +" />;
  }
  return (
    <ItemsList
      items={flatItems}
      sort={sort}
      onSort={setSort}
      query={query}
      onOpenItem={(id) => router.go('item_card', { id })}
    />
  );
}

function renderRoomsTab(
  state: LoadState,
  expanded: ReadonlySet<string>,
  toggle: (id: string) => void,
  query: string,
  router: RouterApi,
): JSX.Element {
  return renderBody(state, expanded, toggle, query, router);
}

// Кнопка-иконка на шапке (история). Нейтральный кружок (hh-ket, case-09).
const iconButtonStyle: JSX.CSSProperties = {
  width: 38,
  height: 38,
  borderRadius: 19,
  background: T.surface,
  border: `1px solid ${T.border}`,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  padding: 0,
};

// Кнопка-карандаш на шапке собственности/комнаты — точка входа в
// edit_property / edit_room (hh-be3, case-34/35). Ghost-стиль, чтобы не
// перетягивать внимание со счётчика. Клик гасит всплытие, иначе аккордеон
// тоже сворачивался бы.
const editButtonStyle: JSX.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 15,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  padding: 0,
};

// Кнопка ручного добавления — точка входа в AddPickerSheet (hh-22x).
const addButtonStyle: JSX.CSSProperties = {
  width: 38,
  height: 38,
  borderRadius: 19,
  background: T.brand,
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  padding: 0,
};

function renderBody(
  state: LoadState,
  expanded: ReadonlySet<string>,
  toggle: (id: string) => void,
  query: string,
  router: RouterApi,
): JSX.Element {
  if (state.kind === 'loading') {
    return <CenteredNote text="Загрузка…" />;
  }
  if (state.kind === 'error') {
    return <CenteredNote text={`Не удалось загрузить: ${state.message}`} tone="danger" />;
  }
  const needle = query.trim().toLowerCase();
  // Фильтруем дерево по совпадению имени узла ИЛИ имени любой вложенной вещи —
  // узел остаётся видимым, если что-то внутри него совпало (aidepo-63.6).
  const orphans = needle.length === 0 ? state.orphans : state.orphans.filter((o) => matches(o.name, needle));
  const properties =
    needle.length === 0 ? state.properties : state.properties.filter((p) => propertyMatches(p, needle));
  // «Без места» — постоянный псевдо-узел дерева помещений (aidepo-63.3): виден
  // ВСЕГДА без фильтра (даже пустым), а под фильтром — только если совпало само
  // название группы либо одна из orphan-вещей.
  const orphanLabelHit = needle.length === 0 || matches('без места', needle);
  const showOrphanGroup = orphanLabelHit || orphans.length > 0;
  if (needle.length > 0 && !showOrphanGroup && properties.length === 0) {
    return <CenteredNote text={`Ничего не нашлось по «${query.trim()}»`} />;
  }
  return (
    <>
      {showOrphanGroup && (
        <OrphanGroup
          orphans={orphans}
          open={expanded.has(ORPHAN_GROUP_ID) || needle.length > 0}
          onToggle={() => toggle(ORPHAN_GROUP_ID)}
          onOpenItem={(id) => router.go('item_card', { id })}
        />
      )}
      {properties.map((p) => (
        <PropertyNodeView
          key={p.id}
          property={p}
          expanded={expanded}
          toggle={toggle}
          needle={needle}
          router={router}
        />
      ))}
    </>
  );
}

// ─── Property ────────────────────────────────────────────────────────────────

interface PropertyNodeViewProps {
  readonly property: PropertyTreeNode;
  readonly expanded: ReadonlySet<string>;
  readonly toggle: (id: string) => void;
  /** Lowercased filter needle; empty string = no filter (aidepo-63.6). */
  readonly needle: string;
  readonly router: RouterApi;
}

function PropertyNodeView({
  property,
  expanded,
  toggle,
  needle,
  router,
}: PropertyNodeViewProps): JSX.Element {
  const open = expanded.has(property.id);
  // Под фильтром показываем только подходящие узлы/вещи; комнату оставляем,
  // если совпало её имя ИЛИ что-то внутри (aidepo-63.6).
  const rooms =
    needle.length === 0 ? property.rooms : property.rooms.filter((r) => roomMatches(r, needle));
  const directItems =
    needle.length === 0 ? property.items : property.items.filter((i) => matches(i.name, needle));
  const directEquipment =
    needle.length === 0
      ? property.equipment
      : property.equipment.filter((e) => matches(e.name, needle));
  // Под активным фильтром раскрываем ветку принудительно, чтобы совпадения
  // были видны без ручного разворота (aidepo-63.6).
  const showOpen = open || needle.length > 0;
  // Бейдж = сколько вещей и оборудования хранится в собственности (прямо +
  // во всех комнатах). Раньше число мешало комнаты с вещами и не сходилось
  // со счётчиком в шапке (hh-cvy.3, case-10). Комнаты показаны строкой ниже.
  const storedCount =
    property.items.length +
    property.equipment.length +
    property.rooms.reduce((sum, r) => sum + r.items.length + r.equipment.length, 0);
  const isEmpty =
    property.rooms.length === 0 &&
    property.items.length === 0 &&
    property.equipment.length === 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div onClick={() => toggle(property.id)} style={branchHeaderStyle}>
        <Chevron open={open} size={16} />
        {/* aidepo-2: иконка собственности подбирается по названию. */}
        <Icon
          name={entityIcon('property', { name: property.name })}
          size={20}
          color={T.ink2}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>
            {property.name}
          </div>
          <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>
            {property.rooms.length > 0
              ? `${property.rooms.length} помещений`
              : 'Помещения не заданы'}
          </div>
        </div>
        <span style={{ fontSize: 11.5, color: T.ink3, fontWeight: 500 }}>
          {storedCount}
        </span>
        <EditHeaderButton
          label={`Править собственность «${property.name}»`}
          onEdit={() => router.go('edit_property', { id: property.id })}
        />
      </div>
      {showOpen && (
        <div style={{ marginLeft: 16, marginTop: 6 }}>
          {rooms.map((room) => (
            <RoomNodeView
              key={room.id}
              room={room}
              expanded={expanded}
              toggle={toggle}
              needle={needle}
              router={router}
            />
          ))}
          {directEquipment.map((eq) => (
            <EquipmentLeaf
              key={eq.id}
              equipment={eq}
              onClick={() => router.go('eq_card', { id: eq.id })}
            />
          ))}
          <ItemList
            items={directItems}
            expanded={expanded}
            toggle={toggle}
            onOpenItem={(id) => router.go('item_card', { id })}
          />
          {isEmpty && <EmptyNote text="Пусто" />}
        </div>
      )}
    </div>
  );
}

// ─── Room ────────────────────────────────────────────────────────────────────

interface RoomNodeViewProps {
  readonly room: RoomNode;
  readonly expanded: ReadonlySet<string>;
  readonly toggle: (id: string) => void;
  /** Lowercased filter needle; empty string = no filter (aidepo-63.6). */
  readonly needle: string;
  readonly router: RouterApi;
}

function RoomNodeView({
  room,
  expanded,
  toggle,
  needle,
  router,
}: RoomNodeViewProps): JSX.Element {
  const open = expanded.has(room.id);
  const count = room.items.length + room.equipment.length;
  // Имя комнаты совпало → показываем всё содержимое; иначе оставляем только
  // совпавшие вещи/оборудование (aidepo-63.6).
  const roomNameHit = needle.length === 0 || matches(room.name, needle);
  const items = roomNameHit ? room.items : room.items.filter((i) => matches(i.name, needle));
  const equipment = roomNameHit
    ? room.equipment
    : room.equipment.filter((e) => matches(e.name, needle));
  // Под фильтром раскрываем комнату принудительно (aidepo-63.6).
  const showOpen = open || needle.length > 0;
  return (
    <div style={{ marginTop: 6 }}>
      <div onClick={() => toggle(room.id)} style={roomHeaderStyle}>
        <Chevron open={open} size={14} />
        {/* hh-345: ручная иконка комнаты либо авто-подбор по названию. */}
        <Icon
          name={entityIcon('room', { icon: room.icon, name: room.name })}
          size={18}
          color={T.ink2}
        />
        <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: T.ink }}>
          {room.name}
        </div>
        <span style={{ fontSize: 11.5, color: T.ink3, fontWeight: 500 }}>{count}</span>
        <EditHeaderButton
          label={`Править комнату «${room.name}»`}
          onEdit={() => router.go('edit_room', { id: room.id })}
        />
      </div>
      {showOpen && (
        <div style={{ marginLeft: 18, marginTop: 4 }}>
          {equipment.map((eq) => (
            <EquipmentLeaf
              key={eq.id}
              equipment={eq}
              onClick={() => router.go('eq_card', { id: eq.id })}
            />
          ))}
          <ItemList
            items={items}
            expanded={expanded}
            toggle={toggle}
            onOpenItem={(id) => router.go('item_card', { id })}
          />
          {count === 0 && <EmptyNote text="пусто" />}
        </div>
      )}
    </div>
  );
}

// ─── Item list (handles container nesting) ──────────────────────────────────

interface ItemListProps {
  readonly items: readonly ItemNode[];
  readonly expanded: ReadonlySet<string>;
  readonly toggle: (id: string) => void;
  readonly onOpenItem: (id: string) => void;
}

function ItemList({
  items,
  expanded,
  toggle,
  onOpenItem,
}: ItemListProps): JSX.Element {
  // Ids referenced as a containerId by some sibling item → those are containers.
  const containerIds = new Set<string>();
  for (const it of items) {
    if (it.containerId != null) containerIds.add(it.containerId);
  }
  // Top-level items in this scope: those not nested inside a sibling container.
  const topLevel = items.filter(
    (it) => it.containerId == null || !containerIds.has(it.containerId),
  );

  return (
    <>
      {topLevel.map((it) => {
        const isContainer = containerIds.has(it.id);
        if (!isContainer) {
          return <ItemLeaf key={it.id} item={it} onClick={() => onOpenItem(it.id)} />;
        }
        const inside = items.filter((c) => c.containerId === it.id);
        const open = expanded.has(it.id);
        return (
          <div key={it.id}>
            <ContainerLeaf
              item={it}
              open={open}
              count={inside.length}
              onToggle={() => toggle(it.id)}
            />
            {open && (
              <div style={{ marginLeft: 18 }}>
                {inside.map((c) => (
                  <ItemLeaf
                    key={c.id}
                    item={c}
                    onClick={() => onOpenItem(c.id)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

// ─── Flat items list (вкладка «Вещи», aidepo-63.6) ──────────────────────────

interface ItemsListProps {
  readonly items: readonly FlatItem[];
  readonly sort: ItemsSort;
  readonly onSort: (s: ItemsSort) => void;
  /** Raw filter text (not yet lowercased). */
  readonly query: string;
  readonly onOpenItem: (id: string) => void;
}

// Плоский список ВСЕХ вещей одним уровнем: вещи из дерева + orphans. У вещи без
// места — подпись «— без места» прямо в строке, без отдельной секции (aidepo-63.6).
function ItemsList({ items, sort, onSort, query, onOpenItem }: ItemsListProps): JSX.Element {
  const needle = query.trim().toLowerCase();
  const filtered = needle.length === 0 ? items : items.filter((it) => matches(it.name, needle));
  const sorted = sortFlatItems(filtered, sort);
  return (
    <>
      <div style={sortRowStyle}>
        <Icon name="sliders" size={15} color={T.ink3} />
        <select
          value={sort}
          onChange={(e) => onSort(e.currentTarget.value as ItemsSort)}
          aria-label="Сортировка"
          style={selectStyle}
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: T.ink3, fontWeight: 500 }}>
          {sorted.length}
        </span>
      </div>
      {sorted.length === 0 ? (
        <CenteredNote text={`Ничего не нашлось по «${query.trim()}»`} />
      ) : (
        sorted.map((it) => (
          <FlatItemRow key={it.id} item={it} onClick={() => onOpenItem(it.id)} />
        ))
      )}
    </>
  );
}

interface FlatItemRowProps {
  readonly item: FlatItem;
  readonly onClick: () => void;
}

function FlatItemRow({ item, onClick }: FlatItemRowProps): JSX.Element {
  return (
    <div onClick={onClick} style={flatRowStyle}>
      {/* hh-345: ручная иконка вещи либо авто-подбор по названию. */}
      <Icon name={entityIcon('item', { icon: item.icon, name: item.name })} size={18} color={T.ink3} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={rowNameStyle}>{item.name}</div>
        <div style={item.isOrphan ? rowPlaceOrphanStyle : rowPlaceStyle}>{item.place}</div>
      </div>
      {item.quantity > 1 && <span style={leafSubStyle}>×{item.quantity}</span>}
      <Icon name="chevR" size={16} color={T.ink4} />
    </div>
  );
}

// ─── Segmented control (Вещи / Помещения, aidepo-63.6) ──────────────────────

interface SegmentButtonProps {
  readonly label: string;
  readonly active: boolean;
  readonly onClick: () => void;
}

function SegmentButton({ label, active, onClick }: SegmentButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        flex: 1,
        padding: '8px 12px',
        borderRadius: 999,
        background: active ? T.surface : 'transparent',
        color: active ? T.ink : T.ink3,
        border: 'none',
        boxShadow: active ? T.shadow1 : 'none',
        fontSize: 13,
        fontWeight: 650,
        fontFamily: T.font,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

// ─── Leaves ──────────────────────────────────────────────────────────────────

interface ItemLeafProps {
  readonly item: ItemNode;
  readonly onClick: () => void;
}

function ItemLeaf({ item, onClick }: ItemLeafProps): JSX.Element {
  return (
    <div onClick={onClick} style={leafStyle}>
      {/* hh-345: ручная иконка вещи либо авто-подбор по названию. */}
      <Icon
        name={entityIcon('item', { icon: item.icon, name: item.name })}
        size={16}
        color={T.ink3}
      />
      <div style={leafNameStyle}>{item.name}</div>
      {item.isOrphan && (
        <Chip tone="safety" style={{ flexShrink: 0 }}>
          без места
        </Chip>
      )}
      {item.quantity > 1 && (
        <span style={leafSubStyle}>×{item.quantity}</span>
      )}
      <Icon name="chevR" size={14} color={T.ink4} />
    </div>
  );
}

interface ContainerLeafProps {
  readonly item: ItemNode;
  readonly open: boolean;
  readonly count: number;
  readonly onToggle: () => void;
}

function ContainerLeaf({
  item,
  open,
  count,
  onToggle,
}: ContainerLeafProps): JSX.Element {
  // hh-345: ручную иконку контейнера уважаем; по умолчанию для контейнера —
  // «коробка», она отражает его роль лучше авто-подбора по названию.
  const containerIcon = isIconName(item.icon) ? item.icon : 'box';
  return (
    <div onClick={onToggle} style={leafStyle}>
      <Chevron open={open} size={13} />
      <Icon name={containerIcon} size={16} color={T.ink3} />
      <div style={leafNameStyle}>{item.name}</div>
      <span style={leafSubStyle}>{count} внутри</span>
    </div>
  );
}

interface EquipmentLeafProps {
  readonly equipment: EquipmentNode;
  readonly onClick: () => void;
}

function EquipmentLeaf({ equipment, onClick }: EquipmentLeafProps): JSX.Element {
  const sub = equipment.brand ?? equipment.category;
  return (
    <div onClick={onClick} style={leafStyle}>
      {/* hh-345: ручная иконка оборудования либо авто-подбор по категории. */}
      <Icon
        name={entityIcon('equipment', {
          icon: equipment.icon,
          name: equipment.name,
          category: equipment.category,
        })}
        size={16}
        color={T.brand}
      />
      <div style={leafNameStyle}>{equipment.name}</div>
      {sub.length > 0 && (
        <span style={{ ...leafSubStyle, color: T.brand }}>{sub}</span>
      )}
      <Icon name="chevR" size={14} color={T.ink4} />
    </div>
  );
}

// ─── Orphan group ────────────────────────────────────────────────────────────

interface OrphanGroupProps {
  readonly orphans: readonly OrphanItem[];
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly onOpenItem: (id: string) => void;
}

function OrphanGroup({
  orphans,
  open,
  onToggle,
  onOpenItem,
}: OrphanGroupProps): JSX.Element {
  // «Без места» — постоянный псевдо-узел (aidepo-63.3): показываем всегда, даже
  // пустым. Пустой узел остаётся свёрнутым по дефолту (его нет в expanded), но
  // раскрытым показывает пояснение вместо списка.
  const isEmpty = orphans.length === 0;
  return (
    <div style={{ marginBottom: 16 }}>
      <div
        onClick={onToggle}
        style={{
          ...branchHeaderStyle,
          background: T.safetySoft,
          border: '1px solid #E8D7A8',
        }}
      >
        <Chevron open={open} size={16} color={T.safety} />
        <Icon name="pkg" size={20} color={T.safety} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.safety }}>
            Без места
          </div>
          <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>
            {isEmpty ? 'нет вещей без места' : `${orphans.length} вещей без привязки`}
          </div>
        </div>
        <span style={{ fontSize: 11.5, color: T.safety, fontWeight: 600 }}>
          {orphans.length}
        </span>
      </div>
      {open && (
        <div style={{ marginLeft: 16, marginTop: 4 }}>
          {isEmpty ? (
            <EmptyNote text="Здесь появляются вещи без комнаты и собственности" />
          ) : (
            orphans.map((it) => (
              <div key={it.id} onClick={() => onOpenItem(it.id)} style={leafStyle}>
                {/* hh-345: ручная иконка либо авто-подбор по названию. */}
                <Icon
                  name={entityIcon('item', { icon: it.icon, name: it.name })}
                  size={16}
                  color={T.ink3}
                />
                <div style={leafNameStyle}>{it.name}</div>
                <Chip tone="safety" style={{ flexShrink: 0 }}>
                  без места
                </Chip>
                <Icon name="chevR" size={14} color={T.ink4} />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Small parts ─────────────────────────────────────────────────────────────

interface ChevronProps {
  readonly open: boolean;
  readonly size: number;
  readonly color?: string;
}

function Chevron({ open, size, color }: ChevronProps): JSX.Element {
  return (
    <Icon
      name="chevD"
      size={size}
      color={color ?? T.ink3}
      style={{
        transform: open ? 'none' : 'rotate(-90deg)',
        transition: 'transform 0.2s',
        flexShrink: 0,
      }}
    />
  );
}

// Карандаш на шапке ветки. stopPropagation обязателен — иначе клик дойдёт до
// onClick шапки и аккордеон свернётся (hh-be3, case-34/35).
interface EditHeaderButtonProps {
  readonly label: string;
  readonly onEdit: () => void;
}

function EditHeaderButton({ label, onEdit }: EditHeaderButtonProps): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onEdit();
      }}
      style={editButtonStyle}
    >
      <Icon name="edit" size={16} color={T.ink3} />
    </button>
  );
}

function EmptyNote({ text }: { readonly text: string }): JSX.Element {
  return (
    <div
      style={{
        fontSize: 12.5,
        color: T.ink4,
        padding: '8px 12px',
        fontStyle: 'italic',
      }}
    >
      {text}
    </div>
  );
}

interface CenteredNoteProps {
  readonly text: string;
  readonly tone?: 'danger';
}

function CenteredNote({ text, tone }: CenteredNoteProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: '40px 24px',
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 500,
        color: tone === 'danger' ? T.danger : T.ink3,
      }}
    >
      {text}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function subtitleFor(state: LoadState): string {
  if (state.kind !== 'ready') return ' ';
  const propCount = state.properties.length;
  let roomCount = 0;
  // Считаем вещи И оборудование — та же семантика, что у бейджей собственности
  // и комнат, чтобы счётчик в шапке сходился с их суммой (hh-cvy.3, case-10).
  let storedCount = state.orphans.length;
  for (const p of state.properties) {
    roomCount += p.rooms.length;
    storedCount += p.items.length + p.equipment.length;
    for (const r of p.rooms) storedCount += r.items.length + r.equipment.length;
  }
  return `${propCount} объектов · ${roomCount} комнат · ${storedCount} в учёте`;
}

function describeError(err: unknown): string {
  return humanizeError(err, 'load');
}

// Совпадение имени с уже-приведённым к lowercase запросом (aidepo-63.6).
function matches(name: string, needle: string): boolean {
  return name.toLowerCase().includes(needle);
}

// Собственность видна под фильтром, если совпало её имя ИЛИ что-то внутри.
function propertyMatches(p: PropertyTreeNode, needle: string): boolean {
  if (matches(p.name, needle)) return true;
  if (p.items.some((i) => matches(i.name, needle))) return true;
  if (p.equipment.some((e) => matches(e.name, needle))) return true;
  return p.rooms.some((r) => roomMatches(r, needle));
}

// Комната видна под фильтром, если совпало её имя ИЛИ что-то внутри.
function roomMatches(r: RoomNode, needle: string): boolean {
  if (matches(r.name, needle)) return true;
  if (r.items.some((i) => matches(i.name, needle))) return true;
  return r.equipment.some((e) => matches(e.name, needle));
}

// Плоский список ВСЕХ вещей: обходим дерево (вещи собственности + вещи комнат)
// и добавляем orphans. Место — человекочитаемая строка для подписи и сортировки
// «Место»; у orphan — NO_PLACE_LABEL (aidepo-63.6).
function collectFlatItems(
  properties: readonly PropertyTreeNode[],
  orphans: readonly OrphanItem[],
): readonly FlatItem[] {
  const out: FlatItem[] = [];
  for (const p of properties) {
    for (const it of p.items) out.push(toFlat(it, p.name));
    for (const r of p.rooms) {
      const place = `${p.name} · ${r.name}`;
      for (const it of r.items) out.push(toFlat(it, place));
    }
  }
  for (const o of orphans) {
    out.push({
      id: o.id,
      name: o.name,
      icon: o.icon ?? null,
      quantity: o.quantity,
      isOrphan: true,
      place: NO_PLACE_LABEL,
      createdAt: o.createdAt ?? null,
    });
  }
  return out;
}

function toFlat(it: ItemNode, place: string): FlatItem {
  return {
    id: it.id,
    name: it.name,
    icon: it.icon ?? null,
    quantity: it.quantity,
    isOrphan: it.isOrphan,
    place: it.isOrphan ? NO_PLACE_LABEL : place,
    createdAt: it.createdAt ?? null,
  };
}

// Сортировка плоского списка. «Имя» — А-Я по локали ru; «Место» — по подписи
// места (вещи без места уезжают в конец); «Недавно» — по createdAt убыванием
// (отсутствующий createdAt считаем старейшим). Сорт стабильно-копирующий —
// исходный массив не мутируем (aidepo-63.6).
function sortFlatItems(items: readonly FlatItem[], sort: ItemsSort): readonly FlatItem[] {
  const copy = items.slice();
  if (sort === 'name') {
    copy.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  } else if (sort === 'place') {
    copy.sort((a, b) => {
      // Вещи без места — в конец, среди прочих — по подписи места, затем по имени.
      if (a.isOrphan !== b.isOrphan) return a.isOrphan ? 1 : -1;
      const byPlace = a.place.localeCompare(b.place, 'ru');
      return byPlace !== 0 ? byPlace : a.name.localeCompare(b.name, 'ru');
    });
  } else {
    copy.sort((a, b) => {
      const ta = a.createdAt ?? '';
      const tb = b.createdAt ?? '';
      // ISO 8601 сравнивается лексикографически как хронология; убывание = новые сверху.
      if (ta === tb) return a.name.localeCompare(b.name, 'ru');
      return tb.localeCompare(ta);
    });
  }
  return copy;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const containerStyle: JSX.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
};

const scrollStyle: JSX.CSSProperties = {
  flex: 1,
  overflow: 'auto',
  padding: '4px 16px 24px',
};

const branchHeaderStyle: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 14px',
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: T.r3,
  cursor: 'pointer',
};

const roomHeaderStyle: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px',
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: 12,
  cursor: 'pointer',
};

const leafStyle: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '9px 12px',
  marginTop: 4,
  borderRadius: 10,
  cursor: 'pointer',
};

const leafNameStyle: JSX.CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 13.5,
  color: T.ink,
  fontWeight: 550,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const leafSubStyle: JSX.CSSProperties = {
  fontSize: 11.5,
  color: T.ink3,
  fontWeight: 600,
  flexShrink: 0,
};

// ─── Controls (сегменты + инлайн-поиск, aidepo-63.6) ────────────────────────

const controlsStyle: JSX.CSSProperties = {
  padding: '0 16px 8px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

// Сегмент-контрол — «таблетка» с подсветкой активного сегмента.
const segmentRowStyle: JSX.CSSProperties = {
  display: 'flex',
  gap: 4,
  padding: 4,
  background: T.bgAlt,
  borderRadius: 999,
};

const searchBoxStyle: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 14px',
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: 14,
};

const inputStyle: JSX.CSSProperties = {
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontFamily: T.font,
  fontSize: 15,
  color: T.ink,
};

const clearButtonStyle: JSX.CSSProperties = {
  width: 24,
  height: 24,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  borderRadius: 12,
  cursor: 'pointer',
  padding: 0,
  flexShrink: 0,
};

// ─── Flat items list styles (aidepo-63.6) ───────────────────────────────────

const sortRowStyle: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '4px 2px 6px',
};

const selectStyle: JSX.CSSProperties = {
  border: `1px solid ${T.border}`,
  background: T.surface,
  borderRadius: 10,
  padding: '6px 10px',
  fontFamily: T.font,
  fontSize: 12.5,
  fontWeight: 600,
  color: T.ink2,
  cursor: 'pointer',
};

const flatRowStyle: JSX.CSSProperties = {
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: 12,
  padding: '11px 14px',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginTop: 8,
  cursor: 'pointer',
};

const rowNameStyle: JSX.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: T.ink,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const rowPlaceStyle: JSX.CSSProperties = {
  fontSize: 12,
  color: T.ink3,
  marginTop: 2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// Подпись места у вещи без привязки — амбер, чтобы «без места» выделялось.
const rowPlaceOrphanStyle: JSX.CSSProperties = {
  ...rowPlaceStyle,
  color: T.safety,
  fontWeight: 600,
};
